/**
 * Quest Sell Panel
 * Selling UI for quest sell objectives, using the modular FishSellList component.
 * Shows per-objective progress with fish sell cards capped at remaining objective progress.
 */

import { useGameStore } from '@/state';
import { sellManager } from '@/systems/selling/SellManager';
import { FishStack, Quality, QuestDefinition, QuestObjective } from '@/types';
import { FishSellList } from '@/components/selling';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { BackButton } from '@/components/layout/BackButton';

interface QuestSellPanelProps {
  quest: QuestDefinition;
  marketId: string;
  onBack: () => void;
}

export function QuestSellPanel({ quest, marketId, onBack }: QuestSellPanelProps) {
  const inventory = useGameStore((state) => state.playerState.inventory);
  const activeQuest = useGameStore((state) =>
    state.playerState.quests.activeQuests.find((q) => q.questId === quest.id)
  );

  if (!activeQuest) {
    return (
      <div className="p-6 text-center text-gray-400">
        <p>Quest not found or not active.</p>
        <button onClick={onBack} className="mt-4 text-blue-400 hover:text-blue-300">
          Go Back
        </button>
      </div>
    );
  }

  // Get sell objectives only
  const sellObjectives = quest.objectives.filter((obj) => obj.type === 'sell_fish');

  // Handle selling fish for a specific objective
  const handleSell = (
    objective: QuestObjective,
    definitionId: string,
    quality: Quality,
    count: number
  ) => {
    const stack = inventory.stacks.find(
      (s) => s.definitionId === definitionId && s.quality === quality
    );
    if (!stack || stack.count === 0) return;

    const objectiveProgress = activeQuest.objectives.find((o) => o.objectiveId === objective.id);
    if (!objectiveProgress) return;

    const remaining = objectiveProgress.targetValue - objectiveProgress.currentValue;
    const sellCount = Math.min(stack.count, remaining, count);
    if (sellCount <= 0) return;

    sellManager.sellFish(
      [{ definitionId, quality, count: sellCount }],
      marketId
    );
  };

  return (
    <ScrollableView>
      {/* Header */}
      <div>
        <BackButton label="Quest Board" onClick={onBack} className="mb-4" />

        <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 rounded-xl border-2 border-green-600 p-6">
          <div className="flex items-center gap-3 mb-3">
            {quest.isTutorial && <span className="text-yellow-400 text-2xl">⭐</span>}
            <h1 className="text-2xl font-bold text-white">{quest.name}</h1>
          </div>
          <p className="text-gray-300">{quest.description}</p>
        </div>
      </div>

      {/* Sell Objectives */}
      <div className="space-y-4">
        {sellObjectives.map((objective) => {
          const objectiveProgress = activeQuest.objectives.find(
            (o) => o.objectiveId === objective.id
          );
          if (!objectiveProgress) return null;

          // Check if this objective has specific species requirements
          const requiredSpecies = objective.filters?.speciesId
            ? Array.isArray(objective.filters.speciesId)
              ? objective.filters.speciesId
              : [objective.filters.speciesId]
            : null;

          // Get all fish stacks that match the requirements
          const availableStacks = requiredSpecies
            ? inventory.stacks.filter((s) => requiredSpecies.includes(s.definitionId))
            : inventory.stacks;

          const totalOwned = availableStacks.reduce((sum, stack) => sum + stack.count, 0);
          const remaining = objectiveProgress.targetValue - objectiveProgress.currentValue;
          const percentage = Math.round(
            (objectiveProgress.currentValue / objectiveProgress.targetValue) * 100
          );

          // Cap function: limit per stack to remaining objective progress
          const getMaxSellable = (stack: FishStack) =>
            Math.min(stack.count, remaining);

          return (
            <div
              key={objective.id}
              className="bg-gray-800 rounded-xl border-2 border-gray-700 p-6"
            >
              {/* Objective Header */}
              <div className="mb-4">
                <h3 className="text-lg font-bold text-white mb-2">
                  {objective.description}
                </h3>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Progress</span>
                  <span
                    className={
                      objectiveProgress.completed
                        ? 'text-green-400 font-bold'
                        : 'text-white'
                    }
                  >
                    {objectiveProgress.currentValue} / {objectiveProgress.targetValue}
                    {objectiveProgress.completed && ' ✓'}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${objectiveProgress.completed ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>

              {/* Inventory Status */}
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-400">Fish in Inventory</span>
                  <span className="text-lg font-bold text-white">{totalOwned}</span>
                </div>

                {!objectiveProgress.completed ? (
                  <FishSellList
                    stacks={availableStacks}
                    buyerId={marketId}
                    getMaxSellable={getMaxSellable}
                    onSell={(defId, quality, count) =>
                      handleSell(objective, defId, quality, count)
                    }
                    emptyMessage="No fish in inventory"
                    emptySubMessage="Go catch some fish first!"
                  />
                ) : null}
              </div>

              {objectiveProgress.completed && (
                <div className="mt-4 w-full bg-green-900/30 border-2 border-green-700 text-green-400 px-4 py-3 rounded-lg font-bold text-center">
                  ✓ Objective Complete!
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quest Complete - Claim Rewards */}
      {activeQuest.completed && !activeQuest.claimedRewards && (
        <div className="bg-gradient-to-r from-yellow-900/40 to-orange-900/40 rounded-xl border-2 border-yellow-500 p-6">
          <h2 className="text-2xl font-bold text-white mb-3 text-center">
            🎉 Quest Complete! 🎉
          </h2>
          <p className="text-center text-gray-300 mb-4">
            All objectives finished! Return to the quest board to claim your rewards.
          </p>
          <button
            onClick={onBack}
            className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 text-white px-6 py-4 rounded-lg font-bold transition-all transform hover:scale-105 shadow-xl"
          >
            Claim Rewards at Quest Board →
          </button>
        </div>
      )}
    </ScrollableView>
  );
}
